﻿import React, { useState, useEffect } from 'react';
import { Intent } from '@blueprintjs/core';

import { Tooltip } from 'components/tooltip';
import { Breadcrumbs } from 'components/navigation';
import { PageRestricted } from 'pages/errors/page-restricted';
import { UserAccess } from 'components/user-access';
import { FormTextInput, FormSelect } from "components/form-fields";
import { ButtonSave, Button } from 'components/buttons'
import { ListingTable } from "components/listing";
import { ShowHide } from 'components/layout';
import { AccountService, GlobalSearchService, UserService } from 'services';
import { NotificationToaster } from "components/notifications";
import moment from 'moment';


export function GlobalSearch(props) {

    const [hasSearched, setHasSearched] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [entityType, setEntityType] = useState({});
    const [loadingData, setLoadingData] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [listingData, setListingData] = useState(null);
    const requiredAction = "Internal";
    const entities = [
        { id: "user", name: "Users" },
        { id: "asset", name: "Assets" },
        { id: "employee", name: "Employees" },
        { id: "job", name: "Jobs" }
    ]
    const [formValid, setFormValid] = useState(false);

    const [tableRows, setTableRows] = useState([]);
    const [tableHeaders, setTableHeaders] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const noDataMessage = "No results found";
    const defaultPageNumber = 1;
    const defaultPageSize = 25;
    const minSearchLength = 3;
    const minJobSearchLength = 9;

    useEffect(() => {
        if (entityType?.id?.toUpperCase() === 'EMPLOYEE') {
            setTableHeaders(["Identifier", "Name", "Status", "Type", "Reference", "Card number", "License number", "Start date", "End date", "Account", "Location", "Actions"]);
        } else if (entityType?.id?.toUpperCase() === 'ASSET') {
            setTableHeaders(["Identifier", "Reg/ID", "Status", "Type", "Ownership", "Chassis number", "Fleet number", "Start date", "End date", "Account", "Location", "On hold", "Actions"]);
        } else if (entityType?.id?.toUpperCase() === 'JOB') {
            setTableHeaders(["Identifier", "Status", "Product", "Due date", "Scheduled date", "Account", "Location", "Postcode", "Internal Id", "Asset Type", "Registration", "Actions"]);
        } else {
            setTableHeaders(["Identifier", "Info", "Account", "Actions"]);
        }

        setTableRows([]);
        setTotalRecords(0);
        setHasSearched(false);
        setListingData(null);
    }, [entityType])

    function onPagingChange(newPageNumber, newPageSize) {
        fetchSearchResults(newPageNumber, newPageSize);
    }

    function onSearchTextChange(event) {
        setSearchTerm(event.target.value);
    }

    function onSearch() {
        fetchSearchResults(defaultPageNumber, defaultPageSize);
    }

    function fetchSearchResults(pageNumber, pageSize) {
        setLoadingData(true);
        GlobalSearchService.search(entityType.id, pageSize, pageNumber, searchTerm)
            .then((searchResponse) => {
                setHasSearched(true);
                setLoadingData(false);
                setListingData(searchResponse);
            })
            .catch(function (error) {
                NotificationToaster.show(Intent.DANGER, error);
                setLoadingData(false);
            });
    }

    useEffect(() => {
        AccountService.getAllAccounts()
            .then((response) => {
                setAccounts(response.data || []);
            }, () => {
                NotificationToaster.show(Intent.DANGER, "Could not retrieve accounts, please try again.");
            })
            .catch((error) => {
                NotificationToaster.show(Intent.DANGER, "Could not retrieve accounts, please try again.");
            });
    }, []);


    useEffect(() => {
        if (listingData && Array.isArray(listingData.data)) {
            const mappedRows = listingData.data.map(function (row) {
                let matchedAccount = accounts.find(account => account.id === row.accountId);
                return formatSearchResponse(row, matchedAccount);
            });
            setTableRows(mappedRows);
            setTotalRecords(listingData.totalCount);
        }
    }, [listingData, accounts]);

    function formatSearchResponse(row, account) {
        const isRestricted = account && account.restricted === true;
        if (entityType.id.toUpperCase() === 'EMPLOYEE') {
            return [
                row.entityId,
                row.fullName,
                row.status.name,
                row.type?.name,
                <span className='break-word'>{row.reference}</span>,
                row.cardNumber,
                row.employeeLicenceNumber,
                formatDate(row.startDate),
                formatDate(row.endDate),
                `${row.accountName} - ${row.memberNumber}`,
                row.location?.name,
                <Tooltip key={`account-${row.accountId}-switch-account-button`} content={isRestricted ? "You do not have permission to switch to this account" : "Switch to employee profile"}>
                    <Button
                        onClick={() => switchToAccount(row.accountId, row.entityId, false)}
                        icon="swap-horizontal"
                        iconOnly={true}
                        large={false}
                        minimal={true}
                        disabled={isRestricted}
                    />
                </Tooltip>  
            ];
        } else if (entityType.id.toUpperCase() === 'ASSET') {
            return [
                row.entityId,
                row.registration,
                row.assetStatus?.name,
                row.assetType?.name,
                row.assetOwnership?.name,
                row.chassisNumber,
                <span className='break-word'>{row.fleetNumber}</span>,
                formatDate(row.startDate),
                formatDate(row.endDate),
                `${row.accountName} - ${row.memberNumber}`,
                row.location?.name,
                formatBool(row.onHold),
                <Tooltip key={`account-${row.accountId}-switch-account-button`} content={isRestricted ? "You do not have permission to switch to this account" : switchTooltip("asset", row.onHold)}>
                    <Button
                        onClick={() => switchToAccount(row.accountId, row.entityId, row.onHold)}
                        icon="swap-horizontal"
                        iconOnly={true}
                        large={false}
                        minimal={true}
                        disabled={isRestricted}
                    />
                </Tooltip>
            ];
        } else if (entityType.id.toUpperCase() === 'JOB') {
            return [
                row.entityId,
                row.jobStatus,
                row.productName,
                formatDate(row.dueDate),
                formatDate(row.scheduledDate),
                `${row.accountName} - ${row.memberNumber}`,
                row.locationName,
                row.postCode,
                row.jobPubsId,
                row.assetType,
                row.registation,
                <Tooltip key={`account-${row.accountId}-switch-account-button`} content={isRestricted ? "You do not have permission to switch to this account" : "Switch to job details"}>
                    <Button
                        key="job-button"
                        onClick={() => switchToAccount(row.accountId, row.entityId, false)}
                        icon="swap-horizontal"
                        iconOnly={true}
                        large={false}
                        minimal={true}
                        disabled={isRestricted}
                    />
                </Tooltip>
            ];
        } else {
            return [
                row.entityName,
                row.entityMetadata,
                `${row.accountName} - ${row.memberNumber}`,
                <Tooltip key={`account-${row.accountId}-switch-account-button`} content={isRestricted ? "You do not have permission to switch to this account" : "Switch to account"}>
                    <Button
                        onClick={() => switchToAccount(row.accountId, row.entityId, false)}
                        icon="swap-horizontal"
                        iconOnly={true}
                        large={false}
                        minimal={true}
                        disabled={isRestricted}
                    />
                </Tooltip>
            ];
        }
    }

    function formatDate(date) {
        return date ? moment(date).format('DD/MM/YYYY') : '';
    }

    function formatBool(bool) {
        return bool ? 'True' : 'False';
    }

    function onEntityTypeSelect(item) {
        setEntityType(item);
    }

    function switchTooltip(entityTypeToSwitch, onHold) {
        return onHold ? `Switch to holding area` : `Switch to ${entityTypeToSwitch} profile`
    }

    function switchToAccount(accountId, entityId, onHold) {
        UserService.switchAccount(accountId).then(function () {
            switch (entityType.id.toUpperCase()) {

                case "USER":
                    window.location.href = `/user/${entityId}`;
                    break;
                case "EMPLOYEE":
                    window.location.href = `${window.env.VISION_URL}/#/Drivers/RedirectToProfile?employeeId=${entityId}`;
                    break;
                case "ASSET":
                    if (onHold) {
                        window.location.href = `${window.env.VISION_URL}/#/Alerts/NEWVEHICLE`;
                    } else {
                        window.location.href = `/asset/${entityId}/details?area=MMV`;
                    }
                    break;
                case "JOB":
                    window.location.href = `/job/${entityId}/job-details`;
                    break;
                default:
                    window.location.reload();
                    break;
            }
        });
    }

    useEffect(function () {
        var valid = searchTerm.trim().length > minSearchLength && entityType.id != null;

        //check if the search term is an integer
        if (valid && entityType.id !== undefined && entityType.id.toUpperCase() === 'JOB') {
            valid = !isNaN(searchTerm) && parseInt(searchTerm) === parseFloat(searchTerm) && searchTerm.indexOf('.') === -1 && searchTerm.length <= minJobSearchLength;
        }

        setFormValid(valid);
    }, [searchTerm, entityType])

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <div className="row">

                    <Breadcrumbs items={props.breadcrumbs} />

                    <h1>Global Search</h1>
                    <div className="intro-text">
                        <p>This page is designed to help you locate the account/member that certain entities belong to in Vision.</p>
                    </div>

                    <div className="inline-items">
                        <FormTextInput placeholder="Search Term" onChange={onSearchTextChange} value={searchTerm} large disabled={loadingData} id="entity-search-field" />

                        <FormSelect
                            disabled={loadingData}
                            items={entities}
                            onItemSelect={onEntityTypeSelect}
                            selectedValue={entityType.id}
                            large={true}
                            placeholder="Search type"
                        ></FormSelect>

                        <ButtonSave text="Search" icon="search" intent="primary" onClick={onSearch} disabled={loadingData} simpleDisabled={!formValid} />
                    </div>

                    <ShowHide
                        evaluator={hasSearched}
                        show={(
                            <ListingTable
                                id="listing-table-seach"
                                headers={tableHeaders}
                                data={tableRows}
                                totalRecordCount={totalRecords}
                                onPagingChange={onPagingChange}
                                loadingData={loadingData}
                                noDataMessage={noDataMessage}
                                sortable
                            />

                        )}
                    />
                </div>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );

}